import { useEffect, useCallback, useRef, useState } from 'react';
import { api } from '../lib/api';
import { mimeFor, type MemberMeta } from '../lib/archive';
import { holdScreenAwake, wakeLockSupported } from '../lib/wakelock';
import { rememberJob, savedJobFor, forgetJob } from '../lib/resume';
import type { IngestProgress } from '../worker/ingest';
import type { ParsedMessage } from '../lib/parser';

const pct = (d: number, t: number) => (t ? Math.round((d / t) * 100) : 0);
const mb = (n: number) => (n / 1e6).toFixed(1);

export function Importer({ accountKey, onDone, startWith, onStarted, onRunning, pickRef,
                          existingFor }: {
  accountKey: Uint8Array;
  onDone: (manifest: Record<string, MemberMeta>) => void | Promise<void>;
  /** A file already chosen during a preview, so nobody picks it twice. */
  startWith?: File | null;
  onStarted?: () => void;
  /** So the page knows not to offer a reload mid-upload. */
  onRunning?: (running: boolean) => void;
  /** Recebe a função que abre o seletor, para o "+" do cabeçalho chamar. */
  pickRef?: { current: (() => void) | null };
  /** Which conversation this export is, and what the archive already holds
   *  for it, so a second export can be joined to the first. Only the page
   *  can answer: reading needs the API and the key, and deciding whether a
   *  nearly-identical name is the same chat needs the person. */
  existingFor?: (chatId: string, title: string) =>
    Promise<{ messages: ParsedMessage[]; chatId?: string; title?: string }>;
}) {
  const [progress, setProgress] = useState<IngestProgress | null>(null);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const run = useCallback((file: File) => {
    setProgress({ phase: 'scanning', done: 0, total: 0 });
    const worker = new Worker(new URL('../worker/ingest.ts', import.meta.url), { type: 'module' });

    /**
     * A Worker that dies says nothing. Without this the screen sits on
     * "Reading the archive…" for ever and the person watching has no way to
     * tell a slow import from a dead one — which is worse than a failure,
     * because a failure can at least be reported.
     */
    worker.onerror = (e) => {
      worker.terminate();
      const where = e.filename ? ` (${e.filename.split('/').pop()}:${e.lineno})` : '';
      setProgress({ phase: 'error', message: `The importer stopped: ${e.message || 'unknown error'}${where}` });
    };
    worker.onmessageerror = () => {
      worker.terminate();
      setProgress({ phase: 'error', message: 'The importer sent something this browser could not read.' });
    };

    worker.onmessage = async (e: MessageEvent<IngestProgress>) => {
      // The worker has no storage of its own, so the page keeps the job.
      if (e.data.phase === 'job') { rememberJob(file, e.data.importId); return; }
      // Not progress: the worker's token expired mid-import and it wants a new
      // one. Refreshing lives here, on the main thread, so it has to be served.
      if (e.data.phase === 'need-token') {
        worker.postMessage({ type: 'token', token: (await api.freshToken()) ?? '' });
        return;
      }
      // The worker has worked out which conversation this file is, and wants
      // what is already stored for it. Decrypting a conversation can take a
      // moment on a long one, so the wait is shown rather than looking hung.
      if (e.data.phase === 'need-existing') {
        const { chatId, title } = e.data;
        setProgress({ phase: 'merging', held: 0, fresh: 0 });
        let answer = { messages: [] as ParsedMessage[] };
        try { answer = (await existingFor?.(chatId, title)) ?? answer; } catch { /* a new chat */ }
        worker.postMessage({ type: 'existing', ...answer });
        return;
      }
      setProgress(e.data);
      if (e.data.phase === 'done') {
        forgetJob();
        const geometry: Record<string, MemberMeta> = {};
        for (const [cid, g] of Object.entries(e.data.geometry))
          geometry[cid] = { plainLength: g.plainLength, frameSize: g.frameSize, mime: mimeFor(g.kind) };
        await onDone(geometry);
        worker.terminate();
        setTimeout(() => setProgress(null), 4000);
      }
      if (e.data.phase === 'error') {
        // The only way to see inside a failure on somebody else's phone. No
        // content: the stage, the message this code wrote, and which bundle
        // was running — that last one because "did the fix reach them" has
        // been the unanswered question twice now.
        void api.report({
          stage: 'import', message: e.data.message,
          build: document.querySelector<HTMLScriptElement>('script[type="module"][src*="/assets/index-"]')?.src.split('/').pop(),
          bytes: file.size,
        });
      }
      // The job is deliberately *not* forgotten here. A failed upload is the
      // case resume exists for, and dropping it would mean a network blip
      // costs the whole archive again. The loop that failure could cause is
      // bounded in savedJobFor instead, by counting attempts.
      if (e.data.phase === 'error') worker.terminate();
    };
    // savedJobFor counts the attempt and stops offering the job once it has
    // been picked up twice without finishing — a reload is a fresh tab, so the
    // bound has to live in storage rather than in this component.
    const resumeFrom = savedJobFor(file)?.importId;
    void api.freshToken().then((t) => {
      worker.postMessage({ file, accountKey, token: t ?? api.currentToken(), resumeFrom });
    });
  }, [accountKey, onDone, existingFor]);

  // Handed a file from the preview: start it without a second file picker.
  useEffect(() => {
    if (!startWith) return;
    onStarted?.();
    run(startWith);
  }, [startWith, run, onStarted]);

  const busy = progress !== null && progress.phase !== 'done' && progress.phase !== 'error';

  // O botão vive no cabeçalho, mas o input vive aqui — junto do arrastar-e-
  // soltar e do progresso, que são a mesma máquina.
  useEffect(() => {
    if (!pickRef) return;
    pickRef.current = () => input.current?.click();
    return () => { pickRef.current = null; };
  }, [pickRef]);

  // Sem a caixa "solte um zip aqui" não há mais um alvo desenhado, então a
  // janela inteira aceita. O contador existe porque dragleave dispara em cada
  // elemento filho que o ponteiro atravessa na entrada, não só na saída.
  useEffect(() => {
    let depth = 0;
    const carriesFile = (e: DragEvent) =>
      Array.from(e.dataTransfer?.types ?? []).includes('Files');
    const enter = (e: DragEvent) => { if (carriesFile(e)) { depth++; setDrag(true); } };
    const over = (e: DragEvent) => { if (carriesFile(e)) e.preventDefault(); };
    const leave = () => { if (--depth <= 0) { depth = 0; setDrag(false); } };
    const drop = (e: DragEvent) => {
      if (!carriesFile(e)) return;
      e.preventDefault(); depth = 0; setDrag(false);
      const f = e.dataTransfer?.files[0];
      if (f) run(f);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => {
      window.removeEventListener('dragenter', enter);
      window.removeEventListener('dragover', over);
      window.removeEventListener('dragleave', leave);
      window.removeEventListener('drop', drop);
    };
  }, [run]);

  // An import is the one thing here long enough to outlast a screen timeout,
  // and a locked screen suspends the tab and kills it.
  useEffect(() => {
    if (!busy) return;
    return holdScreenAwake();
  }, [busy]);
  useEffect(() => { onRunning?.(busy); }, [busy, onRunning]);

  const label = (() => {
    if (!progress) return null;
    switch (progress.phase) {
      case 'scanning': return 'Reading the archive…';
      case 'encrypting': return `Encrypting ${progress.done} of ${progress.total} files`;
      case 'asking': return 'Asking what is already stored…';
      case 'merging': return progress.held
        ? `Joining ${progress.fresh.toLocaleString()} messages to the ${progress.held.toLocaleString()} already here…`
        : 'Looking for an earlier export of this chat…';
      case 'uploading': return progress.carried
        // Says it for as long as the upload lasts, rather than flashing once:
        // picking a 3.5 GB import back up is worth knowing about throughout.
        ? `Uploading ${progress.done} of ${progress.total} packs — ${progress.carried} already done from last time`
        : `Uploading ${progress.done} of ${progress.total} packs`;
      case 'committing': return 'Finishing…';
      case 'done': {
        // "messages ·" survives every branch that has a count: it is what the
        // suites wait for, and what tells somebody the import got that far.
        const n = progress.messages.toLocaleString();
        const added = progress.heldBefore === undefined
          ? undefined : progress.messages - progress.heldBefore;
        // The same export offered twice. It merged, and the merge was the
        // conversation that was already here — so the honest thing to say is
        // still that nothing arrived, not that nought messages joined.
        if (progress.uploadedBytes === 0 && !added)
          return `Already had all ${progress.alreadyHeld} objects — nothing uploaded`;
        if (added) return `${n} messages · ${added.toLocaleString()} new, joined to what was here`;
        return `${n} messages · ${mb(progress.uploadedBytes)} MB uploaded`;
      }
      case 'error': return progress.shortfallBytes
        // Sem o número escrito à mão: o limite é uma coluna por conta, o
        // console pode levantá-lo, e uma frase que jura "5 GB" mente no dia
        // em que ele muda — como mudou.
        ? `Over your storage cap by ${(progress.shortfallBytes / 1024 ** 3).toFixed(2)} GB — nothing was uploaded`
        : progress.message;
    }
  })();

  const running = progress !== null && progress.phase !== 'done' && progress.phase !== 'error';
  const bar = progress && 'total' in progress && progress.total
    ? pct(progress.done, progress.total)
    : null;

  return (
    <div className={`import ${progress?.phase === 'error' ? 'bad' : ''}`}>
      <input ref={input} id="import-file" type="file" accept=".zip,application/zip" hidden
             onChange={(e) => { const f = e.target.files?.[0]; if (f) run(f); e.target.value = ''; }} />
      {drag && <div className="dropping"><span>Drop the export to import it</span></div>}
      {label && <p className="status">{label}</p>}
      {busy && (
        // True whatever the browser allows: a wake lock stops the screen
        // timing out, and nothing can survive the person switching apps.
        <p className="hint keepopen">
          {wakeLockSupported()
            ? 'Keep this tab open — the screen will stay on until it finishes.'
            : 'Keep this tab open and the screen on until it finishes.'}
        </p>
      )}
      {bar !== null && running && <div className="bar"><i style={{ width: `${bar}%` }} /></div>}
    </div>
  );
}
